import { makeCent } from '@frontend/duck-tape';
import { validators as v, z } from '@frontend/forms';
import { AppShell, XStack } from '@frontend/web-react';
import {
  AppContentWrapper,
  OnboardingCard,
  OnboardingLogoutButton,
  OnboardingPageWrapper,
  UnauthenticatedAppHeader,
} from '@frontend/web/components';
import type { OnboardingCompleteState } from '@frontend/web/components/DomainSpecific/Onboarding/Complete';
import { DownloadAppLink, OnboardingCompleteScreen } from '@frontend/web/components/DomainSpecific/Onboarding/Complete';
import { createFileRoute, handleRedirectBeforeLoad } from '@frontend/web/utils';
import { useRouterState } from '@tanstack/react-router';

const onboardingCompleteStateSchema = v.object({
  onboardingSessionId: v.string().optional(),
  paidToday: z
    .number()
    .transform((n) => makeCent(n))
    .optional(),
  planCode: v.string().optional(),
  promotionId: v.string().optional(),
  promotionName: v.string().optional(),
  purchaseType: z.literal('Subscription').optional(),
  renewalDate: v.string().optional(),
  renewalPlanLabel: v.string().optional(),
  step: z.enum(['complete', 'paymentCapture', 'selectPlan']).optional(),
});

const parseLocationState = (state: unknown): OnboardingCompleteState | undefined => {
  const parsed = onboardingCompleteStateSchema.safeParse(state);
  return parsed.success ? (parsed.data as OnboardingCompleteState) : undefined;
};

const Complete = () => {
  const navigate = Route.useNavigate();
  const routerState = useRouterState();
  const locationState = parseLocationState(routerState.location.state);

  const handleComplete = () => {
    navigate({ to: '/onboarding/profile' });
  };

  return (
    <AppShell>
      <UnauthenticatedAppHeader linkTo={false} rightContent={<OnboardingLogoutButton step="complete" />} />
      <AppContentWrapper className="flex-1">
        <OnboardingPageWrapper currentStep="membership">
          <OnboardingCard>
            <OnboardingCompleteScreen onComplete={handleComplete} state={locationState} />
          </OnboardingCard>
          <XStack className="flex-col sm:flex-row" gap="lg">
            <OnboardingCard>
              <DownloadAppLink platform="ios" />
            </OnboardingCard>
            <OnboardingCard>
              <DownloadAppLink platform="android" />
            </OnboardingCard>
          </XStack>
        </OnboardingPageWrapper>
      </AppContentWrapper>
    </AppShell>
  );
};

export const Route = createFileRoute('/onboarding/complete')({
  beforeLoad: async ({ context, location }) => {
    handleRedirectBeforeLoad({ context, location });
  },
  component: Complete,
});
